import { Link } from "@/lib/router-compat";
import { SupportingVisual } from "./Visuals";
import {
  SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK,
  SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK_ALT,
  SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK_CAPTION,
} from "@/data/blogData";

const linkCls = "text-[#00A3D9] underline font-medium hover:text-[#0B1F3D]";

/** Sections 4–6: scope levels table, cost factors, sequencing. */
export function BodyPart2() {
  return (
    <>
      {/* Section 4: scope levels table */}
      <section
        id="three-common-levels-of-pool-remodeling-scope"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          Three Common Levels of Pool-Remodeling Scope
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            The following categories are planning tools, not rigid construction
            packages.
          </p>
        </div>
        <div className="my-6 overflow-x-auto rounded-xl border border-border shadow-xs">
          <table className="w-full text-sm md:text-base border-collapse min-w-[640px]">
            <thead className="bg-[#0B1F3D] text-white">
              <tr>
                <th className="text-left p-4 font-semibold border-b border-white/15">
                  Scope level
                </th>
                <th className="text-left p-4 font-semibold border-b border-white/15">
                  Typical focus
                </th>
                <th className="text-left p-4 font-semibold border-b border-white/15">
                  When it may fit
                </th>
                <th className="text-left p-4 font-semibold border-b border-white/15">
                  What to evaluate
                </th>
              </tr>
            </thead>
            <tbody className="text-foreground">
              <tr className="bg-card">
                <td className="p-4 font-semibold text-[#0B1F3D] align-top border-b border-border">
                  Interior refresh
                </td>
                <td className="p-4 align-top border-b border-border">
                  Primarily the pool&rsquo;s interior finish
                </td>
                <td className="p-4 align-top border-b border-border">
                  The finish is the main concern and surrounding components
                  remain suitable
                </td>
                <td className="p-4 align-top border-b border-border">
                  Existing surface, preparation needs, finish selection,
                  startup, and coordination with existing tile and coping
                </td>
              </tr>
              <tr className="bg-card">
                <td className="p-4 font-semibold text-[#0B1F3D] align-top border-b border-border">
                  Coordinated pool remodel
                </td>
                <td className="p-4 align-top border-b border-border">
                  Finish plus tile, coping, or other pool-edge components
                </td>
                <td className="p-4 align-top border-b border-border">
                  Multiple connected pool components are worn, dated, damaged,
                  or visually mismatched
                </td>
                <td className="p-4 align-top border-b border-border">
                  Component condition, removal sequence, material transitions,
                  construction interfaces, and final design
                </td>
              </tr>
              <tr className="bg-card">
                <td className="p-4 font-semibold text-[#0B1F3D] align-top">
                  Pool-and-deck transformation
                </td>
                <td className="p-4 align-top">
                  Pool finish and edge components plus deck, pavers, or
                  surrounding outdoor areas
                </td>
                <td className="p-4 align-top">
                  The project extends beyond the water and affects how the
                  entire space looks or functions
                </td>
                <td className="p-4 align-top">
                  Deck area, base conditions, drainage, elevations, access,
                  material coordination, landscaping, and project sequencing
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            A homeowner does not earn extra points for choosing the largest
            scope. The best scope is the one that addresses the actual
            conditions and produces a complete result without replacing sound
            components unnecessarily.
          </p>
        </div>
      </section>

      {/* Section 5: cost factors */}
      <section
        id="what-determines-the-cost-of-a-pool-remodel"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          What Determines the Cost of a Pool Remodel?
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            There is no single meaningful price for &ldquo;a pool remodel&rdquo;
            because the term can describe everything from a focused interior
            project to a coordinated pool-and-deck transformation.
          </p>
          <p>
            Instead of beginning with one broad number, separate the project
            into cost categories.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Pool size, shape, and condition
          </h3>
          <p>
            A larger pool generally involves more surface area and material.
            Curves, steps, benches, attached spas, raised sections, and detailed
            edges can also affect the work involved.
          </p>
          <p>
            The condition discovered after draining or demolition may influence
            preparation. That is why a proposal should explain how unexpected
            conditions are handled rather than pretending every pool will
            require identical work.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Interior-finish selection
          </h3>
          <p>
            Finish systems have different material requirements, appearances,
            textures, preparation instructions, and installation considerations.
          </p>
          <p>
            The proposal should identify the selected finish category and
            clarify what surface preparation, fittings, startup, and related
            work are included.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">Waterline tile</h3>
          <p>Tile cost can vary based on:</p>
          <ul>
            <li>Pool perimeter</li>
            <li>Tile material</li>
            <li>Tile size and pattern</li>
            <li>Installation detail</li>
            <li>Removal and surface preparation</li>
            <li>Grout and setting requirements</li>
            <li>Curves, steps, raised walls, spas, and other transitions</li>
          </ul>
          <p>
            A small tile sample may look inexpensive by itself while creating
            substantially more installation detail across an entire pool
            perimeter.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">Coping</h3>
          <p>
            Coping cost depends on the perimeter, material, dimensions, profile,
            removal needs, cuts, corners, transitions, and how it meets the
            deck.
          </p>
          <p>
            Coping should be priced as a defined part of the project rather than
            hidden inside a vague &ldquo;pool-edge work&rdquo; allowance.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Pool deck and pavers
          </h3>
          <p>
            Deck scope is often one of the biggest variables in a broader
            remodel because square footage can change dramatically from one
            property to another.
          </p>
          <p>Cost factors may include:</p>
          <ul>
            <li>Demolition and removal</li>
            <li>Existing slab or base conditions</li>
            <li>New base preparation</li>
            <li>Drainage work</li>
            <li>Material selection</li>
            <li>Pattern and layout</li>
            <li>Edge details and cuts</li>
            <li>Access for crews and materials</li>
            <li>Transitions to the home, yard, or adjacent hardscape</li>
          </ul>
          <p>
            Precision also offers{" "}
            <Link to="/services/driveway-patio-pavers" className={linkCls}>
              driveway, patio, and paver services
            </Link>{" "}
            for projects that extend beyond the immediate pool deck.
          </p>

          <SupportingVisual
            src={SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK}
            alt={SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK_ALT}
            caption={SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK_CAPTION}
            width={900}
            height={600}
          />

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Features and design changes
          </h3>
          <p>
            Verified features or changes to the existing configuration add their
            own material, labor, coordination, and potential approval
            requirements.
          </p>
          <p>
            Anything affecting plumbing, electrical work, structural conditions,
            barriers, or the physical configuration should be identified clearly
            before a price is treated as complete.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Access and site conditions
          </h3>
          <p>
            A pool with wide side-yard access presents a different construction
            plan from one behind narrow gates, finished landscaping,
            air-conditioning equipment, fences, or limited staging space.
          </p>
          <p>Access can affect:</p>
          <ul>
            <li>Equipment size</li>
            <li>Labor</li>
            <li>Material handling</li>
            <li>Demolition removal</li>
            <li>Landscape protection</li>
            <li>Scheduling and sequencing</li>
          </ul>
          <p>
            The useful question is not simply, &ldquo;What does a pool remodel
            cost?&rdquo; It is, &ldquo;What exact work is included, what could
            change the price, and which assumptions does the proposal
            make?&rdquo; For a more focused estimate framework, review the
            current{" "}
            <Link
              to="/blog/pool-resurfacing-cost-south-florida"
              className={linkCls}
            >
              South Florida pool-resurfacing cost guide
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Section 6: sequencing */}
      <section
        id="how-to-plan-the-project-in-the-right-sequence"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          How to Plan the Project in the Right Sequence
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            Good sequencing protects completed work and reduces the chance that
            one decision has to be undone for another.
          </p>
          <p>
            The exact order varies by project, but planning generally follows
            this progression.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            1. Evaluate the existing conditions
          </h3>
          <p>Walk the entire pool area&mdash;not just the interior.</p>
          <p>Document:</p>
          <ul>
            <li>Surface wear or discoloration</li>
            <li>Loose or damaged tile</li>
            <li>Coping separation or movement</li>
            <li>Cracked or uneven deck areas</li>
            <li>Drainage concerns</li>
            <li>Recurring water loss</li>
            <li>Changes in the way the pool functions</li>
            <li>Features or materials you want to preserve</li>
          </ul>
          <p>
            A photograph can help document a concern, but it cannot reliably
            determine its cause.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            2. Define the project goal
          </h3>
          <p>Decide whether the priority is to:</p>
          <ul>
            <li>Restore a worn pool</li>
            <li>Modernize the appearance</li>
            <li>Coordinate mismatched materials</li>
            <li>Replace the pool deck</li>
            <li>Improve how the area is used</li>
            <li>Connect the pool to a larger outdoor project</li>
            <li>Address a combination of those goals</li>
          </ul>
          <p>
            Without a clear goal, material shopping can take over the project
            before the correct scope has been defined.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            3. Separate &ldquo;keep&rdquo; from &ldquo;change&rdquo;
          </h3>
          <p>Identify which components may remain:</p>
          <ul>
            <li>Interior finish</li>
            <li>Waterline tile</li>
            <li>Coping</li>
            <li>Deck or pavers</li>
            <li>Steps, benches, spas, or other existing features</li>
            <li>Surrounding patios and landscape elements</li>
          </ul>
          <p>
            Keeping a component can control scope and preserve materials that
            still work. But the interfaces between old and new elements must be
            considered.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            4. Resolve connected conditions
          </h3>
          <p>
            Before choosing final finishes, look at how the components meet.
          </p>
          <p>Ask:</p>
          <ul>
            <li>Will deck work affect the coping?</li>
            <li>Will coping replacement affect the tile?</li>
            <li>
              Does the existing drainage plan still work with the new deck?
            </li>
            <li>
              Will a future patio or outdoor feature require disturbing newly
              completed work?
            </li>
            <li>Are elevations and transitions compatible?</li>
            <li>
              Is there an unexplained condition that should be evaluated before
              cosmetic work begins?
            </li>
          </ul>
          <p>
            This is where a well-planned remodel separates itself from a
            collection of unrelated upgrades.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            5. Coordinate materials
          </h3>
          <p>Compare the interior finish, tile, coping, and deck together.</p>
          <p>
            Evaluate samples under conditions similar to the property whenever
            possible. Look at them:
          </p>
          <ul>
            <li>In sun and shade</li>
            <li>Dry and wet</li>
            <li>Beside the home&rsquo;s exterior</li>
            <li>Near existing landscaping and hardscape</li>
            <li>At different times of day</li>
          </ul>
          <p>
            Screens and edited project photos are useful for inspiration, but
            they do not reproduce texture, reflectivity, natural variation, or
            perceived color perfectly.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            6. Confirm the written scope
          </h3>
          <p>
            Before work begins, the proposal should make the project boundaries
            understandable.
          </p>
          <p>It should identify:</p>
          <ul>
            <li>What is being removed</li>
            <li>What is being retained</li>
            <li>What materials or allowances are included</li>
            <li>What preparation is expected</li>
            <li>Which conditions could change the scope</li>
            <li>Who is responsible for each project component</li>
            <li>How optional upgrades are approved</li>
            <li>What completion, startup, care, or handoff steps apply</li>
          </ul>
          <p>
            The word &ldquo;remodel&rdquo; is not a substitute for an itemized
            scope. The{" "}
            <Link
              to="/blog/pool-resurfacing-process-south-florida"
              className={linkCls}
            >
              pool-resurfacing process guide
            </Link>{" "}
            explains what generally happens from draining and surface
            preparation through finish application, filling, startup, and early
            care.
          </p>
        </div>
      </section>
    </>
  );
}
